/**
 * Redactions API - Document redaction workflow management.
 *
 * Provides operations for:
 * - Redaction CRUD
 * - Apply/reject workflow
 * - Redaction sets for batch operations
 * - Statistics
 */

import apiClient, { type RequestConfig } from './client';
import type {
  BatchApplyResult,
  DocumentRedactionsParams,
  Redaction,
  RedactionCreate,
  RedactionPreview,
  RedactionReject,
  RedactionSet,
  RedactionSetCreate,
  RedactionSetsParams,
  RedactionStatistics,
  RedactionUpdate,
} from './redactions-types';

const ENDPOINT = '/redactions';

export const redactionsApi = {
  // ---------------------------------------------------------------------------
  // Redaction CRUD
  // ---------------------------------------------------------------------------

  /**
   * Create a new redaction for a document.
   */
  async createRedaction(
    request: RedactionCreate,
    config?: RequestConfig
  ): Promise<Redaction> {
    const { data } = await apiClient.post<Redaction>(
      ENDPOINT,
      request,
      config
    );
    return data;
  },

  /**
   * Get a single redaction by ID.
   */
  async getRedaction(
    redactionId: string,
    config?: RequestConfig
  ): Promise<Redaction> {
    const { data } = await apiClient.get<Redaction>(
      `${ENDPOINT}/${redactionId}`,
      config
    );
    return data;
  },

  /**
   * Update a pending redaction.
   */
  async updateRedaction(
    redactionId: string,
    request: RedactionUpdate,
    config?: RequestConfig
  ): Promise<Redaction> {
    const { data } = await apiClient.put<Redaction>(
      `${ENDPOINT}/${redactionId}`,
      request,
      config
    );
    return data;
  },

  /**
   * Delete a pending redaction.
   */
  async deleteRedaction(
    redactionId: string,
    config?: RequestConfig
  ): Promise<void> {
    await apiClient.delete(`${ENDPOINT}/${redactionId}`, config);
  },

  /**
   * List all redactions for a document.
   */
  async listDocumentRedactions(
    documentId: string,
    params?: DocumentRedactionsParams,
    config?: RequestConfig
  ): Promise<Redaction[]> {
    const { data } = await apiClient.get<Redaction[]>(
      `${ENDPOINT}/document/${documentId}`,
      {
        ...config,
        params: params as unknown as Record<string, string | number | boolean | undefined>,
      }
    );
    return data;
  },

  /**
   * Get preview data for redactions on a specific page.
   */
  async previewRedactions(
    documentId: string,
    pageNumber: number,
    config?: RequestConfig
  ): Promise<RedactionPreview> {
    const { data } = await apiClient.get<RedactionPreview>(
      `${ENDPOINT}/document/${documentId}/preview`,
      {
        ...config,
        params: { page_number: pageNumber },
      }
    );
    return data;
  },

  // ---------------------------------------------------------------------------
  // Redaction Workflow
  // ---------------------------------------------------------------------------

  /**
   * Apply a pending redaction.
   */
  async applyRedaction(
    redactionId: string,
    config?: RequestConfig
  ): Promise<Redaction> {
    const { data } = await apiClient.patch<Redaction>(
      `${ENDPOINT}/${redactionId}/apply`,
      {},
      config
    );
    return data;
  },

  /**
   * Reject a pending redaction.
   */
  async rejectRedaction(
    redactionId: string,
    request: RedactionReject,
    config?: RequestConfig
  ): Promise<Redaction> {
    const { data } = await apiClient.patch<Redaction>(
      `${ENDPOINT}/${redactionId}/reject`,
      request,
      config
    );
    return data;
  },

  // ---------------------------------------------------------------------------
  // Statistics
  // ---------------------------------------------------------------------------

  /**
   * Get redaction statistics for a matter.
   */
  async getMatterStatistics(
    matterId: string,
    config?: RequestConfig
  ): Promise<RedactionStatistics> {
    const { data } = await apiClient.get<RedactionStatistics>(
      `${ENDPOINT}/matter/${matterId}/statistics`,
      config
    );
    return data;
  },

  // ---------------------------------------------------------------------------
  // Redaction Sets
  // ---------------------------------------------------------------------------

  /**
   * Create a new redaction set for a matter.
   */
  async createRedactionSet(
    matterId: string,
    request: RedactionSetCreate,
    config?: RequestConfig
  ): Promise<RedactionSet> {
    const { data } = await apiClient.post<RedactionSet>(
      `${ENDPOINT}/sets/matter/${matterId}`,
      request,
      config
    );
    return data;
  },

  /**
   * Get a redaction set by ID.
   */
  async getRedactionSet(
    setId: string,
    config?: RequestConfig
  ): Promise<RedactionSet> {
    const { data } = await apiClient.get<RedactionSet>(
      `${ENDPOINT}/sets/${setId}`,
      config
    );
    return data;
  },

  /**
   * List all redaction sets for a matter.
   */
  async listRedactionSets(
    matterId: string,
    params?: RedactionSetsParams,
    config?: RequestConfig
  ): Promise<RedactionSet[]> {
    const { data } = await apiClient.get<RedactionSet[]>(
      `${ENDPOINT}/sets/matter/${matterId}`,
      {
        ...config,
        params: params as unknown as Record<string, string | number | boolean | undefined>,
      }
    );
    return data;
  },

  /**
   * Apply all pending redactions in a set.
   */
  async batchApplyRedactionSet(
    setId: string,
    config?: RequestConfig
  ): Promise<BatchApplyResult> {
    const { data } = await apiClient.post<BatchApplyResult>(
      `${ENDPOINT}/sets/${setId}/apply`,
      {},
      config
    );
    return data;
  },

  /**
   * Finalize a redaction set (no more changes allowed).
   */
  async finalizeRedactionSet(
    setId: string,
    config?: RequestConfig
  ): Promise<RedactionSet> {
    const { data } = await apiClient.post<RedactionSet>(
      `${ENDPOINT}/sets/${setId}/finalize`,
      {},
      config
    );
    return data;
  },

  /**
   * Add an existing redaction to a redaction set.
   */
  async addRedactionToSet(
    redactionId: string,
    setId: string,
    config?: RequestConfig
  ): Promise<Redaction> {
    const { data } = await apiClient.post<Redaction>(
      `${ENDPOINT}/${redactionId}/add-to-set/${setId}`,
      {},
      config
    );
    return data;
  },

  /**
   * Use AI to suggest redactions for a document.
   */
  async suggestRedactions(
    documentId: string,
    categories: string[] = ['PII', 'Trade Secret', 'Privileged'],
    config?: RequestConfig
  ): Promise<any[]> {
    const { data } = await apiClient.post<any[]>(
      `${ENDPOINT}/document/${documentId}/suggest`,
      { categories },
      config
    );
    return data;
  },

  /**
   * Convert multiple AI suggestions into pending redactions.
   */
  async batchCreateRedactions(
    documentId: string,
    suggestions: any[],
    redactionSetId?: string,
    config?: RequestConfig
  ): Promise<Redaction[]> {
    const { data } = await apiClient.post<Redaction[]>(
      `${ENDPOINT}/document/${documentId}/batch-create`,
      { suggestions, redaction_set_id: redactionSetId },
      config
    );
    return data;
  },

  /**
   * Get matter-wide redaction summary and health metrics.
   */
  async getMatterRedactionSummary(
    matterId: string,
    config?: RequestConfig
  ): Promise<any> {
    const { data } = await apiClient.get<any>(
      `${ENDPOINT}/matter/${matterId}/summary`,
      config
    );
    return data;
  },
};

export default redactionsApi;
